import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import clock from '~/assets/icons/clock.svg';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { formatTime, getRandomInt } from '~/shared';
import { applesPositions } from './GameFruits.model';
import {
	StyledApple,
	StyledAppleWrapper,
	StyledApplesContainer,
	StyledBottomShadow,
	StyledTimerContainer,
	StyledTopContainer,
	StyledTree,
	StyledTreeLeaves,
} from './GameFruits.styled';

const GameFruits = () => {
	const apples = useMemo(
		() => _.sampleSize(applesPositions, getRandomInt(3, 9)),
		[],
	);
	const [collectedApples, setCollectedApples] = useState<Set<string>>(
		new Set(),
	);
	const [animatingApples, setAnimatingApples] = useState<Set<string>>(
		new Set(),
	);
	const gameTime = useObservable(rage.jobFarmer.getGameTime());
	const [timeLeft, setTimeLeft] = useState(gameTime);
	const [gameResultReported, setGameResultReported] = useState(false);

	const allCollected = collectedApples.size === apples.length;

	const collectApple = (id: string) => {
		if (!collectedApples.has(id)) {
			setAnimatingApples((prev) => new Set(prev).add(id));
			setTimeout(() => {
				setCollectedApples((prev) => {
					const newSet = new Set(prev);
					newSet.add(id);
					return newSet;
				});
				setAnimatingApples((prev) => {
					const newSet = new Set(prev);
					newSet.delete(id);
					return newSet;
				});
			}, 300);
		}
	};

	const timeString = useMemo(
		() => formatTime(Math.max(timeLeft, 0), '{mm}:{ss}'),
		[timeLeft],
	);

	useEffect(() => {
		if (timeLeft <= 0) {
			return;
		}

		const timeout = setTimeout(
			() => setTimeLeft((timeLeft) => timeLeft - 1000),
			1000,
		);
		return () => clearTimeout(timeout);
	}, [timeLeft]);

	useEffect(() => {
		if (timeLeft <= 0 && !gameResultReported) {
			rage.jobFarmer
				.reportLoss()
				.then(() => {
					setGameResultReported(true);
				})
				.catch((err) => rage.notifications.showException(err));
		}
	}, [timeLeft, gameResultReported]);

	useEffect(() => {
		if (allCollected && !gameResultReported) {
			rage.jobFarmer
				.reportWin()
				.then(() => {
					setGameResultReported(true);
				})
				.catch((err) => rage.notifications.showException(err));
		}
	}, [allCollected, gameResultReported]);

	return (
		<div>
			<StyledTopContainer>
				<Text style={{ fontSize: 18, fontWeight: 700, color: '#ffffff' }}>
					Соберите все яблоки
				</Text>
				<StyledTimerContainer>
					<img src={clock} />
					<Text>{timeString}</Text>
				</StyledTimerContainer>
			</StyledTopContainer>
			<StyledTree />
			<StyledApplesContainer>
				{apples.map((apple) => {
					const id = apple.id;
					const visible = !collectedApples.has(id);
					const isAnimating = animatingApples.has(id);
					return (
						<StyledAppleWrapper
							style={{
								left: apple.left,
								bottom: apple.bottom,
								opacity: visible ? 1 : 0,
							}}
							key={apple.id}
							$isAnimating={isAnimating}
							onClick={() => visible && collectApple(id)}
						>
							<StyledApple
								style={{
									rotate: apple.rotate,
								}}
							/>
						</StyledAppleWrapper>
					);
				})}
			</StyledApplesContainer>
			<StyledTreeLeaves />
			<StyledBottomShadow />
		</div>
	);
};

export { GameFruits };
